import { ChangeEvent, FC } from 'react';
import NumberFormat from 'react-number-format';

import { cleanValue, numberFormat } from 'constants/formats';

import MandatoryField from '../MandatoryField';
import IconTooltip from '../Icon/IconTooltip';

type InputNumberProps = {
  id: string;
  value: number;
  label?: string;
  placeholder?: string;
  iconLeft?: string;
  iconRight?: string;
  decimalScale?: number;
  max?: number;
  formGroupClassName?: string;
  onChange: (value: number) => void; // eslint-disable-line
  allowNegative?: boolean;
  required?: boolean;
  lgSize?: boolean;
  disabled?: boolean;
  marginBottom?: number;
  readOnly?: boolean;
  tooltip?: string;
};

const InputNumber: FC<InputNumberProps> = ({
  id,
  value,
  label,
  placeholder = '',
  iconLeft,
  iconRight,
  decimalScale = 10,
  formGroupClassName = '',
  max = Number.MAX_SAFE_INTEGER,
  onChange,
  allowNegative = false,
  required = false,
  lgSize = true,
  disabled = false,
  marginBottom,
  readOnly = false,
  tooltip,
}) => {
  const handleChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const cleanedValue = cleanValue(value);

    onChange(cleanedValue);
  };

  const InputComponent = (
    <NumberFormat
      id={id}
      className={`form-control ${lgSize ? 'form-control-lg' : ''}`}
      value={value}
      onChange={handleChangeInput}
      placeholder={placeholder}
      thousandSeparator={numberFormat.thousandSeparator}
      decimalSeparator={numberFormat.decimalSeparator}
      allowNegative={allowNegative}
      decimalScale={decimalScale}
      readOnly={readOnly}
      disabled={disabled}
      required={required}
      isAllowed={(values) => {
        const { floatValue } = values;
        return (
          floatValue === undefined ||
          floatValue <= Math.min(max, Number.MAX_SAFE_INTEGER)
        );
      }}
    />
  );

  return (
    <div
      className={`form-group ${
        marginBottom !== undefined ? `mb-${marginBottom}` : ''
      }
      ${formGroupClassName}`}
    >
      {label && (
        <label className="form-label d-flex " htmlFor={id}>
          {label}{' '}
          {required && (
            <span className="ml-1">
              <MandatoryField />
            </span>
          )}
          {tooltip && (
            <IconTooltip id={`${id}-tooltip`} tooltip={tooltip} place="right" />
          )}
        </label>
      )}

      <div className="form-control-wrap">
        {iconLeft && (
          <div className="form-icon form-icon-left">
            <em className={`icon ni ${iconLeft}`} />
          </div>
        )}

        {InputComponent}

        {iconRight && (
          <div className="form-icon form-icon-right">
            <em className={`icon ni ${iconRight}`} />
          </div>
        )}
      </div>
    </div>
  );
};

export default InputNumber;
